import { useCallback, useEffect } from 'react';
import { useNodesState, useEdgesState, Node, Edge } from 'reactflow';

import type {
  MissionNodeData,
  StartNodeData,
  QuestNodeData,
  SidePanelMode,
  QuestDependency,
  QuestEdgeType,
  QuestMissionTransition,
  MissionDependency,
  MissionEdgeType
} from '../types';
import {
  QUEST_NODE_WIDTH,
  QUEST_NODE_HEIGHT,
  EXPANDED_MISSION_PADDING,
  EXPANDED_MISSION_HEADER_HEIGHT,
  QUEST_NODE_GAP_X,
  QUEST_NODE_GAP_Y,
  MISSION_NODE_MIN_HEIGHT
} from '../constants';

interface FlowCallbacks {
  onSelectMission: (missionId: number) => void;
  onSelectQuest: (missionId: number, questId: number) => void;
  onAddQuest: (missionId: number) => void;
  onToggleExpand: (missionId: number) => void;
}

const QUEST_EDGE_LABELS: Record<QuestEdgeType, string> = {
  reveal: '표시',
  unlock: '잠금 해제',
  accomplish: '완료',
  lock: '잠금'
};

const MISSION_EDGE_LABELS: Record<MissionEdgeType, string> = {
  reveal: '미션 표시',
  unlock: '미션 잠금 해제',
  accomplish: '미션 완료',
  lock: '미션 잠금'
};

const QUEST_MISSION_TRANSITION_LABELS = {
  goto: '미션 이동'
};

function getQuestPosition(index: number): { x: number; y: number } {
  const col = index % 3;
  const row = Math.floor(index / 3);
  return {
    x: EXPANDED_MISSION_PADDING + col * (QUEST_NODE_WIDTH + QUEST_NODE_GAP_X),
    y: EXPANDED_MISSION_HEADER_HEIGHT + EXPANDED_MISSION_PADDING + row * (QUEST_NODE_HEIGHT + QUEST_NODE_GAP_Y)
  };
}

export function calculateExpandedDimensions(questCount: number): { width: number; height: number } {
  const count = Math.max(questCount, 1);
  const cols = Math.min(count, 3);
  const rows = Math.ceil(count / 3);
  const width =
    EXPANDED_MISSION_PADDING * 2 + cols * QUEST_NODE_WIDTH + Math.max(0, cols - 1) * QUEST_NODE_GAP_X;
  const height =
    EXPANDED_MISSION_HEADER_HEIGHT +
    EXPANDED_MISSION_PADDING * 2 +
    rows * QUEST_NODE_HEIGHT +
    Math.max(0, rows - 1) * QUEST_NODE_GAP_Y;
  return { width: Math.max(width, 280), height };
}

export default function useFlowData(
  missions: Mission[] | undefined,
  sidePanelMode: SidePanelMode,
  callbacks: FlowCallbacks,
  questDependencies: QuestDependency[],
  questMissionTransitions: QuestMissionTransition[],
  missionDependencies: MissionDependency[],
  showAllDependencies: boolean
) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const buildGraph = useCallback(() => {
    if (!missions) return;

    const selectedMissionId =
      sidePanelMode.type !== 'none' ? sidePanelMode.missionId : null;
    const selectedQuestId =
      sidePanelMode.type === 'quest' ? sidePanelMode.questId : null;

    const allNodes: Node[] = [];
    const allEdges: Edge[] = [];

    // START node
    const startNode: Node<StartNodeData> = {
      id: 'start',
      type: 'startNode',
      position: { x: 0, y: 0 },
      data: { label: 'START' },
      draggable: false
    };
    allNodes.push(startNode);

    // Mission nodes (parents must come before children in the array)
    missions.forEach((mission, index) => {
      const missionNode: Node<MissionNodeData> = {
        id: `mission-${mission.id}`,
        type: 'missionNode',
        position: { x: 0, y: 0 },
        data: {
          mission,
          missionIndex: index,
          isSelected: mission.id === selectedMissionId,
          selectedQuestId,
          isExpanded: false,
          onSelectMission: callbacks.onSelectMission,
          onSelectQuest: callbacks.onSelectQuest,
          onAddQuest: callbacks.onAddQuest,
          onToggleExpand: callbacks.onToggleExpand
        },
        style: { height: MISSION_NODE_MIN_HEIGHT }
      };
      allNodes.push(missionNode);

      // Quest nodes are always rendered as independent nodes
      if (mission.quests) {
        mission.quests.forEach((quest, qIndex) => {
          const questNode: Node<QuestNodeData> = {
            id: `quest-${quest.id}`,
            type: 'questNode',
            position: getQuestPosition(qIndex),
            data: {
              quest,
              missionId: mission.id,
              missionIndex: index,
              isSelected: selectedQuestId === quest.id,
              onSelectQuest: callbacks.onSelectQuest
            }
          };
          allNodes.push(questNode);

          allEdges.push({
            id: `mq-${mission.id}-${quest.id}`,
            source: `mission-${mission.id}`,
            target: `quest-${quest.id}`,
            type: 'missionQuestEdge'
          });
        });
      }
    });

    // Mission-to-mission flow edges
    if (missions.length > 0) {
      allEdges.push({
        id: 'start-to-first',
        source: 'start',
        target: `mission-${missions[0].id}`,
        type: 'flowEdge',
        data: { label: 'START' }
      });
    }

    for (let i = 0; i < missions.length - 1; i += 1) {
      allEdges.push({
        id: `edge-${missions[i].id}-${missions[i + 1].id}`,
        source: `mission-${missions[i].id}`,
        target: `mission-${missions[i + 1].id}`,
        type: 'flowEdge',
        data: { label: 'NEXT' }
      });
    }

    const getFocusedNodeIds = () => {
      const focusedNodeIds = new Set<string>();

      if (sidePanelMode.type === 'quest') {
        focusedNodeIds.add(`mission-${sidePanelMode.missionId}`);
        focusedNodeIds.add(`quest-${sidePanelMode.questId}`);
        return focusedNodeIds;
      }

      if (sidePanelMode.type === 'mission') {
        const focusedMission = missions.find((mission) => mission.id === sidePanelMode.missionId);
        focusedNodeIds.add(`mission-${sidePanelMode.missionId}`);
        for (const quest of focusedMission?.quests ?? []) {
          focusedNodeIds.add(`quest-${quest.id}`);
        }
      }

      return focusedNodeIds;
    };

    const focusedNodeIds = getFocusedNodeIds();
    const shouldRenderDependencyEdge = (sourceId: string, targetId: string) => {
      if (showAllDependencies) return true;
      if (sidePanelMode.type === 'none') return false;
      return focusedNodeIds.has(sourceId) || focusedNodeIds.has(targetId);
    };

    // Mission dependency edges (derived from quest reaction commands)
    for (const dep of missionDependencies) {
      const sourceNodeId = `mission-${dep.sourceMissionId}`;
      const targetNodeId = `mission-${dep.targetMissionId}`;
      if (shouldRenderDependencyEdge(sourceNodeId, targetNodeId)) {
        allEdges.push({
          id: `mdep-${dep.sourceMissionId}-${dep.targetMissionId}-${dep.edgeType}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'missionDependencyEdge',
          data: {
            label: MISSION_EDGE_LABELS[dep.edgeType],
            edgeType: dep.edgeType
          }
        });
      }
    }

    // Quest dependency edges
    const nodeIdSet = new Set(allNodes.map((node) => node.id));

    for (const dep of questMissionTransitions) {
      const sourceNodeId = `quest-${dep.sourceQuestId}`;
      const targetNodeId = `mission-${dep.targetMissionId}`;
      const hasNodes = nodeIdSet.has(sourceNodeId) && nodeIdSet.has(targetNodeId);
      if (hasNodes && shouldRenderDependencyEdge(sourceNodeId, targetNodeId)) {
        allEdges.push({
          id: `qmt-${dep.sourceQuestId}-${dep.targetMissionId}-${dep.edgeType}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'questMissionTransitionEdge',
          data: {
            label: QUEST_MISSION_TRANSITION_LABELS[dep.edgeType],
            edgeType: dep.edgeType
          }
        });
      }
    }

    for (const dep of questDependencies) {
      const sourceNodeId = `quest-${dep.sourceQuestId}`;
      const targetNodeId = `quest-${dep.targetQuestId}`;
      const hasNodes = nodeIdSet.has(sourceNodeId) && nodeIdSet.has(targetNodeId);
      if (hasNodes && shouldRenderDependencyEdge(sourceNodeId, targetNodeId)) {
        allEdges.push({
          id: `qdep-${dep.sourceQuestId}-${dep.targetQuestId}-${dep.edgeType}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'questDependencyEdge',
          data: {
            label: QUEST_EDGE_LABELS[dep.edgeType],
            edgeType: dep.edgeType
          }
        });
      }
    }

    setNodes((prevNodes) => {
      const positionMap = new Map(prevNodes.map((node) => [node.id, node.position]));
      return allNodes.map((node) => {
        const prevPosition = positionMap.get(node.id);
        if (!prevPosition) return node;
        return { ...node, position: prevPosition };
      });
    });
    setEdges(allEdges);
  }, [
    missions,
    sidePanelMode,
    questDependencies,
    questMissionTransitions,
    missionDependencies,
    showAllDependencies,
    callbacks.onSelectMission,
    callbacks.onSelectQuest,
    callbacks.onAddQuest,
    callbacks.onToggleExpand,
    setNodes,
    setEdges
  ]);

  useEffect(() => {
    buildGraph();
  }, [buildGraph]);

  return { nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange };
}
